import { RecognizedMedication, ClarificationRequest } from "./types";
import { AIConfig } from "../../../types/landingPage";

export interface BusinessDecision {
  decision: "pass" | "fail" | "clarify";
  reasoning: string;
  flaggedMedications: {
    medication: RecognizedMedication;
    reason: string;
    ruleApplied: string;
  }[];
  clarificationNeeded?: ClarificationRequest[];
}

export function makeBusinessDecision(
  medications: RecognizedMedication[],
  aiConfig?: AIConfig,
  clarificationNeeded?: ClarificationRequest[]
): BusinessDecision {
  // Check if clarification is needed first
  if (clarificationNeeded && clarificationNeeded.length > 0) {
    return {
      decision: "clarify",
      reasoning: "We need more information about your medications to determine eligibility.",
      flaggedMedications: [],
      clarificationNeeded,
    };
  }

  if (!medications || medications.length === 0) {
    // If minimumCategories is set, no medications means fail
    if (aiConfig?.minimumCategories && aiConfig.minimumCategories > 0) {
      return {
        decision: "fail",
        reasoning: aiConfig.minimumCategoriesMessage ||
          `This study requires at least ${aiConfig.minimumCategories} medication(s).`,
        flaggedMedications: [],
      };
    }
    return {
      decision: "pass",
      reasoning: "No medications reported. Patient is eligible to continue with the study.",
      flaggedMedications: [],
    };
  }

  if (!aiConfig?.medicationCategories) {
    return {
      decision: "pass",
      reasoning: "No medication restrictions defined for this study. Patient is eligible to continue.",
      flaggedMedications: [],
    };
  }

  const flaggedMedications: BusinessDecision["flaggedMedications"] = [];
  let shouldDisqualify = false;

  // Apply business rules from the pre-screening config
  for (const med of medications) {
    // Find matching category for this medication
    const category = aiConfig.medicationCategories.find((cat) =>
      cat.medications.some((configMed) => {
        const medName = med.name.toLowerCase().trim();
        const configMedName = configMed.toLowerCase().trim();
        
        const matches = (
          medName === configMedName || // Exact match
          medName.includes(configMedName) || // Med name contains config med
          configMedName.includes(medName) || // Config med contains med name
          // Handle generic names if available
          (med.genericName && (
            med.genericName.toLowerCase().includes(configMedName) ||
            configMedName.includes(med.genericName.toLowerCase())
          ))
        );
        
        return matches;
      })
    );

    if (category) {
      // Check if this medication is in the excludes list
      const isExcluded = category.excludes?.some(excludedMed =>
        excludedMed.toLowerCase().includes(med.name.toLowerCase()) ||
        med.name.toLowerCase().includes(excludedMed.toLowerCase())
      );

      if (isExcluded) {
        continue;
      }

      // Apply disqualification rules
      if (category.disqualifyIfCurrent && med.status === "current") {
        shouldDisqualify = true;
        flaggedMedications.push({
          medication: med,
          reason: `Currently taking ${med.name}, which is not allowed in this study`,
          ruleApplied: `${category.category}: disqualify if currently taking`
        });
      }

      // Check timeframe rules
      if (category.timeframes && med.status === "current") {
        // For current medications, check if there are specific timeframe restrictions
        const relevantTimeframe = Object.entries(category.timeframes).find(([medPattern]) =>
          med.name.toLowerCase().includes(medPattern.toLowerCase()) ||
          (med.genericName && med.genericName.toLowerCase().includes(medPattern.toLowerCase()))
        );

        if (relevantTimeframe) {
          shouldDisqualify = true;
          flaggedMedications.push({
            medication: med,
            reason: `Currently taking ${med.name}, which has specific timing restrictions for this study`,
            ruleApplied: `${category.category}: ${relevantTimeframe[0]} - disqualify if taken within ${relevantTimeframe[1]}`
          });
        }
      }

      // Check disqualifyWithin rules for past medications
      if (category.disqualifyWithin && (med.status === "past" || med.status === "stopped")) {
        // Note: We can't determine exact timing from status alone, so we flag for review
        flaggedMedications.push({
          medication: med,
          reason: `Previously took ${med.name}. Please verify it was not taken within ${category.disqualifyWithin}`,
          ruleApplied: `${category.category}: disqualify if taken within ${category.disqualifyWithin}`
        });
      }
    }
  }

  // Check minimum categories requirement (inclusion logic)
  if (!shouldDisqualify && aiConfig.minimumCategories && aiConfig.minimumCategories > 0) {
    const matchedCategories = new Set<string>();
    for (const med of medications) {
      if (med.status !== "current") continue;
      const category = aiConfig.medicationCategories.find((cat) =>
        cat.medications.some((configMed) => {
          const medName = med.name.toLowerCase().trim();
          const configMedName = configMed.toLowerCase().trim();
          return (
            medName === configMedName ||
            medName.includes(configMedName) ||
            configMedName.includes(medName) ||
            (med.genericName && (
              med.genericName.toLowerCase().includes(configMedName) ||
              configMedName.includes(med.genericName.toLowerCase())
            ))
          );
        })
      );
      if (category) {
        matchedCategories.add(category.category);
      }
    }

    if (matchedCategories.size < aiConfig.minimumCategories) {
      return {
        decision: "fail",
        reasoning: aiConfig.minimumCategoriesMessage ||
          `This study requires at least ${aiConfig.minimumCategories} medication(s). Only ${matchedCategories.size} qualifying medication(s) found.`,
        flaggedMedications,
      };
    }
  }

  const decision: BusinessDecision = {
    decision: shouldDisqualify ? "fail" : "pass",
    reasoning: shouldDisqualify
      ? `Based on your current medications, you are not eligible for this study. ${flaggedMedications.length} medication(s) conflict with study requirements.`
      : flaggedMedications.length > 0
      ? `Your medications appear compatible with this study, but some may need timing verification.`
      : `Your medications are compatible with this study. You are eligible to continue.`,
    flaggedMedications,
  };

  return decision;
} 